import { memo } from "react";
import { useAllVapes, isInStock } from "@/hooks/useProducts";
import { Loader2, Package, Flame, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ProductCard } from "./ProductCard";

// P0 Performance: Removed framer-motion, lazy loading for above-fold content
// Direct import of ProductCard for faster LCP

export const BestSellers = memo(() => {
  const { data: products, isLoading, error } = useAllVapes(30);
  // Prefer in-stock vapes, but always show 4 even if out of stock
  const inStock = products?.filter(p => isInStock(p)) || [];
  const outOfStock = products?.filter(p => !isInStock(p)) || [];
  const bestsellers = inStock.length >= 4 
    ? inStock.slice(0, 4) 
    : [...inStock, ...outOfStock].slice(0, 4);

  return (
    <section id="bestsellers" className="py-16 md:py-24 relative overflow-hidden bg-background">
      <div className="container mx-auto px-4 relative">
        {/* Header - No motion animations for faster paint */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/15 border border-primary/40 mb-6">
            <Flame className="w-4 h-4 text-primary" />
            <span className="text-sm font-bold text-primary">BÄSTSÄLJARE</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            <span className="text-gradient">THCA Vape</span>{" "}
            – Bästsäljare i Sverige
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
            Magic Sauce vape med <span className="text-primary font-semibold">99 % THCA renhet</span> – labbtestade och lagliga
          </p>
          {bestsellers.length > 0 && (
            <p className="mt-2 text-sm text-accent font-medium">
              {bestsellers.length} produkter i lager
            </p>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-16">
            <p className="text-destructive">Kunde inte ladda produkter.</p>
          </div>
        )}

        {!isLoading && !error && bestsellers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-bold mb-2">Inga produkter</h3>
            <p className="text-muted-foreground text-sm">Produkter visas här snart.</p>
          </div>
        )}

        {bestsellers.length > 0 && (
          <>
            {/* Product grid - simplified DOM, no animation wrapper divs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {bestsellers.map((product, index) => (
                <ProductCard key={product.node.id} product={product} index={index} />
              ))}
            </div>
            
            {/* CTA - No motion */}
            <div className="text-center mt-12">
              <Link to="/kollektioner/thca-vapes">
                <Button size="lg" className="cta-gradient text-base px-10 h-14 font-bold rounded-xl shadow-xl hover:shadow-primary/20 transition-shadow">
                  Se Alla Vapes
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
});

BestSellers.displayName = "BestSellers";
